import type { Variants } from "framer-motion";

/**
 * Shared motion vocabulary for the marketing surface.
 *
 * Reduced-motion strategy
 * -----------------------
 * The app is wrapped in `<MotionConfig reducedMotion="user">` (see App.tsx),
 * which makes framer-motion honor `prefers-reduced-motion: reduce` for all
 * `motion.*` components automatically:
 *   - `transform` (x/y/scale/rotate) animations become INSTANT
 *   - `opacity` still animates (RM spec allows fades; they don't trigger
 *     vestibular discomfort)
 *
 * That covers ~95% of motion in the codebase. The two utilities below need
 * extra help because they're called at render time and produce children:
 *   - `splitChars` shouldn't shatter a headline into N animated spans under
 *     RM (still N spans = N frame commits = jank). It collapses to a single
 *     entry so consumers render the line as one block.
 *   - `letterRise` drops the `filter: blur` keyframe under RM because blur
 *     filters are GPU-expensive and the visual payoff is gone once y/x is
 *     instant.
 *
 * `index.css` also includes a global CSS override that flattens any non-
 * framer animation/transition under RM — that's the last line of defence.
 */

export const easeStandard = [0.22, 1, 0.36, 1] as const;
export const easeEntrance = [0.16, 1, 0.3, 1] as const;

export function prefersReducedMotion(): boolean {
  // First honour the in-app override (Settings → Accessibility → Motion).
  // The MotionPreferenceProvider syncs this attribute to <html> on load and on
  // every change, so this stays in sync regardless of the OS setting.
  if (typeof document !== "undefined") {
    const flag = document.documentElement.dataset.motionReduced;
    if (flag === "true") return true;
    if (flag === "false") return false;
  }
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

const reduce = () => prefersReducedMotion();

export const riseFade: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: easeStandard } },
};

export const softFade: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.7, ease: easeStandard } },
};

export const settle: Variants = {
  hidden: { opacity: 0, y: 28, rotateX: 12 },
  visible: {
    opacity: 1,
    y: 0,
    rotateX: 6,
    transition: { duration: 0.85, ease: easeEntrance },
  },
};

export const staggerChildren = (stagger = 0.07, delay = 0): Variants => ({
  hidden: {},
  visible: { transition: { staggerChildren: stagger, delayChildren: delay } },
});

/**
 * Per-character letter-rise for editorial display headings.
 * Under reduced-motion the blur is skipped (expensive, no longer warranted
 * once the y-translation is instant via MotionConfig).
 */
export const letterRise: Variants = {
  hidden: reduce()
    ? { opacity: 0, y: 0 }
    : { opacity: 0, y: 14, filter: "blur(6px)" },
  visible: reduce()
    ? { opacity: 1, transition: { duration: 0.2 } }
    : {
        opacity: 1,
        y: 0,
        filter: "blur(0px)",
        transition: { duration: 0.7, ease: easeEntrance },
      },
};

/**
 * Splits text into one entry per character so `letterRise` can stagger them.
 * Under reduced-motion this collapses to a SINGLE entry covering the whole
 * line — N×fewer animated nodes, no per-character commit cascade.
 */
export function splitChars(text: string): { char: string; key: string }[] {
  if (reduce()) return [{ char: text, key: text }];
  return Array.from(text).map((c, i) => ({ char: c === " " ? "\u00A0" : c, key: `${c}-${i}` }));
}
